import React, { useState } from 'react';
// import { OrderItem, SelectedBatch, Batch } from '../../types';
import { BsX } from 'react-icons/bs';
import { Batch, OutboundOrderItem, SelectedBatch } from '../../../../api/warehouse/generated/src';

interface BatchModalProps {
  item: OutboundOrderItem;
  selectedBatches: SelectedBatch[];
  onAddBatch: (batch: Batch, quantity: number) => void;
  onRemoveBatch: (batchId: string) => void;
  getTotalSelectedQty: () => number;
  onConfirm: () => void;
  onClose: () => void;
}

interface BatchItemProps {
  batch: Batch;
  maxQty: number;
  onAddBatch: (batch: Batch, quantity: number) => void;
}

const BatchItem: React.FC<BatchItemProps> = ({ batch, maxQty, onAddBatch }) => {
  const [qtyInput, setQtyInput] = useState('');

  const handleAddBatch = () => {
    const quantity = parseInt(qtyInput, 10);
    if (!isNaN(quantity) && quantity > 0 && quantity <= maxQty) {
      onAddBatch(batch, quantity);
      setQtyInput('');
    }
  };

  return (
    <div className="border-2 rounded-lg p-3 bg-white">
      <div className="flex justify-between items-start mb-2">
        <div>
          <p className="font-bold">{batch.batchNumber}</p>
          <p className="text-sm text-gray-600">MHD: {new Date(batch.expiryDate).toLocaleDateString('de-DE')}</p>
          <p className="text-sm text-gray-600">Verfügbar: {batch.availableQty} Stück</p>
        </div>
      </div>
      <div className="flex gap-2">
        <input
          type="number"
          placeholder={`Min: ${Math.min(1, maxQty)}`}
          value={qtyInput}
          onChange={(e) => setQtyInput(e.target.value)}
          className="flex-1 p-2 border rounded text-sm"
          min="1"
          max={maxQty}
        />
        <button
          onClick={handleAddBatch}
          className="bg-blue-600 text-white px-4 py-2 rounded font-semibold hover:bg-blue-700 transition text-sm"
        >
          Hinzufügen
        </button>
      </div>
    </div>
  );
};

export const BatchModal: React.FC<BatchModalProps> = ({
  item,
  selectedBatches,
  onAddBatch,
  onRemoveBatch,
  getTotalSelectedQty,
  onConfirm,
  onClose,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg md:text-xl font-bold mb-2">Chargen auswählen</h3>
        <p className="text-gray-700 mb-4 text-sm md:text-base">{item.description}</p>
        
        <div className="mb-4 bg-blue-50 p-3 rounded-lg">
          <p className="text-sm font-semibold">
            Benötigt: {item.requiredQty - item.shippedQty} Stück
            {item.shippedQty > 0 && (
              <span className="text-blue-600 ml-2">({item.shippedQty} bereits verschickt)</span>
            )}
          </p>
          <p className="text-sm font-semibold text-blue-700">
            Ausgewählt: {getTotalSelectedQty()} Stück
          </p>
          {getTotalSelectedQty() < (item.requiredQty - item.shippedQty) && (
            <p className="text-sm text-orange-600 mt-1">
              Noch {(item.requiredQty - item.shippedQty) - getTotalSelectedQty()} Stück fehlen
            </p>
          )}
        </div>
        
        {selectedBatches.length > 0 && (
          <div className="mb-4 border rounded-lg p-3 bg-gray-50">
            <p className="text-sm font-semibold mb-2">Ausgewählte Chargen:</p>
            <div className="space-y-2">
              {selectedBatches.map((sb, idx) => (
                <div key={idx} className="flex items-center justify-between bg-white p-2 rounded border">
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{sb.batch.batchNumber}</p>
                    <p className="text-xs text-gray-600">
                      MHD: {new Date(sb.batch.expiryDate).toLocaleDateString('de-DE')} | {sb.quantity} Stück
                    </p>
                  </div>
                  <button
                    onClick={() => onRemoveBatch(sb.batch.id)}
                    className="ml-2 text-red-600 hover:text-red-800"
                  >
                    <BsX size={18} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">Verfügbare Chargen:</label>
          <div className="space-y-2">
            {item.batches?.filter(batch =>
              !selectedBatches.some(sb => sb.batch.id === batch.id)
            ).map(batch => (
              <BatchItem
                key={batch.id}
                batch={batch}
                maxQty={Math.min(batch.availableQty, (item.requiredQty - item.shippedQty) - getTotalSelectedQty())}
                onAddBatch={onAddBatch}
              />
            ))}
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-300 text-gray-800 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition"
          >
            Abbrechen
          </button>
          
          {selectedBatches.length > 0 && (
            <button
              onClick={onConfirm}
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-green-700 transition"
            >
              ✓ Bestätigen ({getTotalSelectedQty()} Stück)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};